/**
 * As palavras que ACABARAM de aparecer na bolha "acendem" — o fade por
 * palavra do streaming (o "smooth streaming" do Claude.ai).
 *
 * ★ [owner, 21/09/2026] *"lá a animação vai da palavra, da esquerda para a
 * direita, bem lentinho ela vai ganhando opacidade; no nosso o texto vai
 * renderizando tudo de uma vez, muito esquisito e bruto"*. A revelação por
 * prefixo (`revelacao-de-texto.js`) já entrega palavra a palavra; o que
 * faltava era o olho ver CADA palavra chegar — e isso é opacidade.
 *
 * ⚠️ POR QUE É UM PASSO DE DOM, e não markup. O markdown da bolha é
 * re-renderizado INTEIRO a cada avanço (`AawzMarkdownRender` troca o
 * `innerHTML`), então um `<span>` posto no texto-fonte não sobrevive e
 * injetar HTML cru no markdown é exatamente o que o `allow-html` da persona
 * proíbe. Aqui, depois de cada render, as ÚLTIMAS N palavras do texto
 * renderizado (as que chegaram nos últimos `DURACAO_DO_ACENDER_MS`) são
 * embrulhadas num `<span class="iv2-palavra-nova">` com `animation-delay`
 * NEGATIVO igual à idade delas — assim o fade continua de onde estava mesmo
 * com o DOM trocado por baixo, e vai da esquerda para a direita.
 *
 * ⚠️ Conta por PALAVRAS do texto renderizado, não por offset de caractere: a
 * sintaxe do markdown (`**`, `- `, `[`) some ao renderizar e os offsets não
 * batem; palavras batem, a menos de um marcador de lista — erro de UMA
 * palavra a mais ou a menos no fade, invisível.
 *
 * Puro em relação ao Vue: recebe o elemento e as idades. Sem `?.`/`??`.
 */

/**
 * Quanto tempo uma palavra leva para acender. Também é a janela do rastro, e
 * é o ÚNICO botão do fade (a duração da animação sai daqui, inline).
 * ★ [owner, 21/09/2026] 500ms com pedaços a cada 180ms (`MS_ENTRE_PEDACOS`):
 * dois ou três pedaços acendendo ao mesmo tempo, cada um como um bloco — o
 * chunk do Claude.ai. Com 1000ms a frase inteira parecia acender junto.
 */
export const DURACAO_DO_ACENDER_MS = 500

export const CLASSE_DA_PALAVRA_NOVA = 'iv2-palavra-nova'

const ESPACO_NO_FIM = /\s+$/
const ULTIMA_PALAVRA = /(\S+)(\s*)$/

/**
 * Quantas palavras (separadas por espaço) um trecho de texto acrescenta.
 *
 * @param {string} trecho O que entrou entre dois avanços.
 * @returns {number}
 * @example contarPalavras(' base no panorama') // → 3
 */
export function contarPalavras(trecho) {
  const partes = String(trecho || '')
    .split(/\s+/)
    .filter(Boolean)
  return partes.length
}

/**
 * Os últimos nós de TEXTO do container, do fim para o começo, sem os que
 * são só espaço.
 */
function nosDeTextoDoFim(container) {
  const doc = container.ownerDocument
  const walker = doc.createTreeWalker(container, 4 /* NodeFilter.SHOW_TEXT */, null, false)
  const nos = []
  let no = walker.nextNode()
  while (no) {
    if (no.nodeValue && no.nodeValue.trim()) nos.push(no)
    no = walker.nextNode()
  }
  return nos.reverse()
}

/** O `<span>` que acende uma palavra, com o fade já "adiantado" pela idade. */
function spanDaPalavra(doc, palavra, idadeMs) {
  const span = doc.createElement('span')
  span.className = CLASSE_DA_PALAVRA_NOVA
  span.setAttribute('data-palavra-nova', '')
  const idade = Math.max(0, Math.round(idadeMs))
  span.style.animationDelay = (idade ? '-' + idade : '0') + 'ms'
  // A duração vai INLINE: um botão só (`DURACAO_DO_ACENDER_MS`) para a janela
  // do rastro e para o fade — os dois têm de ser o mesmo número, senão a
  // palavra que sai do rastro no meio do fade acende de uma vez.
  span.style.animationDuration = DURACAO_DO_ACENDER_MS + 'ms'
  span.textContent = palavra
  return span
}

/**
 * Embrulha a ÚLTIMA palavra de um nó de texto num `<span>` e devolve o nó
 * com o que sobrou ANTES dela — ou `null` quando ela era a única palavra.
 */
function embrulharUltimaPalavra(no, idadeMs) {
  const casou = ULTIMA_PALAVRA.exec(no.nodeValue)
  if (!casou) return null
  const pai = no.parentNode
  const antes = no.nodeValue.slice(0, casou.index)
  const span = spanDaPalavra(no.ownerDocument, casou[1], idadeMs)
  pai.insertBefore(span, no)
  if (casou[2]) pai.insertBefore(no.ownerDocument.createTextNode(casou[2]), no)
  if (!antes) {
    pai.removeChild(no)
    return null
  }
  // O que sobra antes da palavra continua sendo o nó original.
  no.nodeValue = antes
  pai.insertBefore(no, span)
  return antes.replace(ESPACO_NO_FIM, '') ? no : null
}

/**
 * Acende as últimas palavras do container.
 *
 * @param {Element} container O elemento com o markdown renderizado.
 * @param {number[]} idadesMs A idade (ms desde que apareceu) de cada palavra
 *   NOVA, da MAIS ANTIGA para a mais nova — a última da lista é a última
 *   palavra do texto.
 * @returns {number} Quantas palavras foram embrulhadas.
 * @example acenderUltimasPalavras(el, [300, 150, 0]) // → 3
 */
export function acenderUltimasPalavras(container, idadesMs) {
  if (!container || !idadesMs || !idadesMs.length) return 0
  const nos = nosDeTextoDoFim(container)
  let restantes = idadesMs.slice()
  let embrulhadas = 0
  for (let i = 0; i < nos.length && restantes.length; i += 1) {
    let no = nos[i]
    while (no && restantes.length) {
      const idade = restantes[restantes.length - 1]
      no = embrulharUltimaPalavra(no, idade)
      restantes = restantes.slice(0, -1)
      embrulhadas += 1
    }
  }
  return embrulhadas
}
